import React, { memo, useCallback, useState, useMemo, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { Button, Icon, TextField, Paper, IconButton, Fab } from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import VoiceRecorder from 'src/context/ai-assistant-v2/components/VoiceRecorder';
import useTranscribe from 'src/api/voice/useTranscribe';
import SelectDropdown, {
  SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { ConversationMessage } from '../hooks/useAiConversation';

import { SupportedGPTModels } from '../types/spreadsheetTypes';
import posthog from 'posthog-js';

// Chat dimension constants
const CHAT_DIMENSIONS = {
  WIDTH: {
    MIN: 400, // Minimum width
    DEFAULT: 1200, // Doubled from 600
  },
  HEIGHT: {
    MIN: 200, // Minimum height
    DEFAULT: 800, // Doubled from 400
  },
  PADDING: 24, // Consistent padding from edges
} as const;

interface AIAssistantChatProps {
  transformedDataLength: number;
  conversation: ConversationMessage[];
  isTransforming: boolean;
  selectedModel: SupportedGPTModels;
  onSubmit: (message: string) => void;
  onModelChange: (model: SupportedGPTModels) => void;
}

// Memoized conversation message component to prevent unnecessary re-renders
const ConversationMessageItem = memo(
  ({ msg, index }: { msg: ConversationMessage; index: number }) => {
    // Format timing display based on message type
    const getTimingDisplay = () => {
      if (msg.isUser) {
        // User messages: show timestamp
        return msg.timestamp.toLocaleTimeString();
      } else {
        // AI messages: show time range, duration, and model
        if (msg.requestStartTime && msg.duration !== undefined) {
          const startTime = msg.requestStartTime.toLocaleTimeString();
          const endTime = msg.timestamp.toLocaleTimeString();
          const modelInfo = msg.model ? ` (model: ${msg.model})` : '';
          return `${startTime} - ${endTime} (${msg.duration} seconds)${modelInfo}`;
        } else {
          // Fallback for AI messages without timing info
          return msg.timestamp.toLocaleTimeString();
        }
      }
    };

    return (
      <MDBox
        key={`${msg.timestamp.getTime()}-${index}`}
        mb={1}
        display="flex"
        justifyContent={msg.isUser ? 'flex-end' : 'flex-start'}
      >
        <MDBox
          sx={{
            maxWidth: '80%',
            p: 1.5,
            borderRadius: '12px',
            backgroundColor: msg.isUser ? '#5991eb' : 'grey.100',
            color: msg.isUser ? '#ffffff' : 'text.primary',
            border: msg.isUser ? 'none' : '1px solid',
            borderColor: msg.isUser ? 'transparent' : 'grey.300',
          }}
        >
          <MDTypography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontWeight: msg.isUser ? 500 : 400,
            }}
          >
            {msg.message}
          </MDTypography>
          <MDTypography
            variant="caption"
            sx={{
              opacity: 0.8,
              fontSize: '0.7rem',
              mt: 0.5,
              display: 'block',
              color: msg.isUser ? 'rgba(255,255,255,0.8)' : 'text.secondary',
            }}
          >
            {getTimingDisplay()}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  },
);

ConversationMessageItem.displayName = 'ConversationMessageItem';

// Custom equality function for memoization
const areEqual = (
  prevProps: AIAssistantChatProps,
  nextProps: AIAssistantChatProps,
) => {
  return (
    prevProps.transformedDataLength === nextProps.transformedDataLength &&
    prevProps.conversation === nextProps.conversation &&
    prevProps.isTransforming === nextProps.isTransforming &&
    prevProps.selectedModel === nextProps.selectedModel &&
    prevProps.onSubmit === nextProps.onSubmit &&
    prevProps.onModelChange === nextProps.onModelChange
  );
};

const AIAssistantChat: React.FC<AIAssistantChatProps> = memo(
  ({
    transformedDataLength: _transformedDataLength,
    conversation,
    isTransforming,
    selectedModel,
    onSubmit,
    onModelChange,
  }) => {
    // Manage input state locally
    const [inputMessage, setInputMessage] = useState('');

    // Collapse/expand state
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Model options with descriptions
    const modelOptions = useMemo<SelectDropdownOption[]>(
      () => [
        {
          id: 'gpt-4o',
          display: 'GPT-4o',
          label:
            'GPT-4o - Balanced speed and accuracy for most transformations',
          value: 'gpt-4o',
        },
        {
          id: 'claude-4-sonnet-20250514',
          display: 'Claude 4 Sonnet',
          label:
            'Claude 4 Sonnet - Best for complex data analysis and transformations',
          value: 'claude-4-sonnet-20250514',
        },
      ],
      [],
    );

    // Find the selected model option
    const selectedModelOption = useMemo(
      () =>
        modelOptions.find((option) => option.value === selectedModel) || null,
      [modelOptions, selectedModel],
    );

    // Handle model change
    const handleModelChange = useCallback(
      (option: SelectDropdownOption | null) => {
        if (option) {
          onModelChange(option.value as SupportedGPTModels);
        }
      },
      [onModelChange],
    );

    // Calculate initial position for bottom-right corner
    const getInitialPosition = useCallback(() => {
      const padding = CHAT_DIMENSIONS.PADDING;
      let width: number = CHAT_DIMENSIONS.WIDTH.DEFAULT;
      let height: number = CHAT_DIMENSIONS.HEIGHT.DEFAULT;

      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Ensure the chat doesn't exceed viewport dimensions
      const maxWidth = viewportWidth - padding * 2;
      const maxHeight = viewportHeight - padding * 2;

      // Adjust dimensions if they exceed viewport
      if (width > maxWidth) {
        width = Math.max(CHAT_DIMENSIONS.WIDTH.MIN, maxWidth);
      }
      if (height > maxHeight) {
        height = Math.max(CHAT_DIMENSIONS.HEIGHT.MIN, maxHeight);
      }

      return {
        x: Math.max(padding, viewportWidth - width - padding), // Right edge with padding
        y: Math.max(padding, viewportHeight - height - padding), // Bottom edge with padding
        width,
        height,
      };
    }, []);

    // Chat dimensions and position state for react-rnd
    const [windowState, setWindowState] = useState<{
      x: number;
      y: number;
      width: number;
      height: number;
    }>(getInitialPosition);

    // Handle drag stop - update position state
    const handleDragStop = useCallback(
      (_e: unknown, data: { x: number; y: number }) => {
        setWindowState((prev) => ({ ...prev, x: data.x, y: data.y }));
      },
      [],
    );

    // Handle resize stop - update size and position state
    const handleResizeStop = useCallback(
      (
        e: MouseEvent | TouchEvent,
        direction: string,
        ref: HTMLElement,
        delta: { width: number; height: number },
        position: { x: number; y: number },
      ) => {
        setWindowState({
          x: position.x,
          y: position.y,
          width: ref.offsetWidth as number,
          height: ref.offsetHeight as number,
        });
      },
      [],
    );

    // Audio state - moved inside component
    const {
      mutate: transcribeAudio,
      isLoading: transcribeAudioLoading,
      error: transcribeAudioError,
    } = useTranscribe((response) => {
      // Populate the text field with transcribed text for user review
      setInputMessage(response.text);
    });

    // Handle recording completion
    const handleRecordingComplete = useCallback(
      (audioFile: File) => {
        posthog.capture('ai_assistant_voice_recording_complete', {
          context: 'ai_assistant',
        });
        transcribeAudio({ audio: audioFile });
      },
      [transcribeAudio],
    );

    // Simple change handler - just update local state
    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputMessage(e.target.value);
      },
      [],
    );

    // Handle submit
    const handleSubmit = useCallback(() => {
      posthog.capture('ai_assistant_send_button_clicked', {
        context: 'ai_assistant',
      });
      onSubmit(inputMessage.trim());
      setInputMessage('');
    }, [inputMessage, onSubmit]);

    // Toggle collapse/expand
    const toggleCollapse = useCallback(() => {
      setIsCollapsed((prev) => {
        posthog.capture('ai_assistant_toggle_minimize', {
          context: 'ai_assistant',
          minimized: !prev,
        });
        return !prev;
      });
    }, []);

    // Keyboard shortcut handling
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'l') {
          event.preventDefault();
          event.stopPropagation(); // Prevent other listeners from firing
          toggleCollapse();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [toggleCollapse]);

    // Handle window resize to maintain bottom-right positioning
    useEffect(() => {
      const handleResize = () => {
        setWindowState((prev) => {
          const padding = CHAT_DIMENSIONS.PADDING;
          let { width, height } = prev;

          // Get viewport dimensions
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          // Ensure the chat doesn't exceed viewport dimensions
          const maxWidth = viewportWidth - padding * 2;
          const maxHeight = viewportHeight - padding * 2;

          // Adjust dimensions if they exceed viewport
          if (width > maxWidth) {
            width = Math.max(CHAT_DIMENSIONS.WIDTH.MIN, maxWidth);
          }
          if (height > maxHeight) {
            height = Math.max(CHAT_DIMENSIONS.HEIGHT.MIN, maxHeight);
          }

          return {
            ...prev,
            x: Math.max(padding, viewportWidth - width - padding),
            y: Math.max(padding, viewportHeight - height - padding),
            width,
            height,
          };
        });
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    // Show collapsed FAB when collapsed
    if (isCollapsed) {
      return (
        <Fab
          color="info"
          onClick={toggleCollapse}
          sx={{
            position: 'fixed',
            bottom: CHAT_DIMENSIONS.PADDING,
            right: CHAT_DIMENSIONS.PADDING,
            zIndex: 1300,
          }}
        >
          <Icon>chat</Icon>
        </Fab>
      );
    }

    return (
      <Rnd
        size={{ width: windowState.width, height: windowState.height }}
        position={{ x: windowState.x, y: windowState.y }}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        minWidth={CHAT_DIMENSIONS.WIDTH.MIN}
        minHeight={CHAT_DIMENSIONS.HEIGHT.MIN}
        bounds="window"
        dragHandleClassName="drag-handle"
        cancel="button, input, textarea, .MuiIconButton-root, .MuiButton-root, .react-select"
        style={{
          zIndex: 1300,
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            border: '1px solid #d0d0d0',
            borderRadius: '12px',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }}
        >
          <MDBox p={2} pb={1}>
            <MDBox
              className="drag-handle"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              sx={{
                cursor: 'grab',
                userSelect: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  '& .drag-icon': {
                    opacity: 0.8,
                    color: 'primary.main',
                  },
                },
                '&:active': {
                  cursor: 'grabbing',
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                },
                borderRadius: '8px',
                p: 1,
                mx: -1,
                transition: 'all 0.2s ease',
              }}
            >
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon
                  className="drag-icon"
                  sx={{
                    fontSize: '16px',
                    color: 'text.secondary',
                    opacity: 0.6,
                    transition: 'all 0.2s ease',
                  }}
                >
                  drag_indicator
                </Icon>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="h6" color="info.main">
                    AI Assistant
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      opacity: 0.6,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                    }}
                  >
                    {navigator.platform.includes('Mac') ? '⌘L' : 'Ctrl+L'}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDBox sx={{ minWidth: 180 }}>
                  <SelectDropdown
                    options={modelOptions}
                    value={selectedModelOption}
                    onChange={handleModelChange}
                    placeholder="Select model..."
                    disabled={isTransforming}
                    size="small"
                    fullWidth={true}
                  />
                </MDBox>
                <IconButton
                  onClick={toggleCollapse}
                  size="small"
                  sx={{
                    backgroundColor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: '8px',
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: 'warning.light',
                      borderColor: 'warning.main',
                      '& .MuiIcon-root': {
                        color: 'white',
                      },
                    },
                    '& .MuiIcon-root': {
                      color: 'grey.600',
                      fontSize: '20px',
                      fontWeight: 'bold',
                    },
                  }}
                >
                  <Icon>remove</Icon>
                </IconButton>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Conversation History - Always render to maintain layout */}
          <MDBox
            px={2}
            pb={1}
            position="relative"
            flex={1}
            display="flex"
            flexDirection="column"
            minHeight={0}
          >
            {conversation.length > 0 ? (
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: '8px',
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  position: 'relative',
                  minHeight: 0,
                  maxHeight: '100%',
                }}
              >
                {conversation.map((msg: ConversationMessage, index: number) => (
                  <ConversationMessageItem
                    key={`${msg.timestamp.getTime()}-${index}`}
                    msg={msg}
                    index={index}
                  />
                ))}
              </Paper>
            ) : (
              // Empty conversation area to maintain layout
              <MDBox
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  px: 3,
                }}
              >
                <MDTypography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  Ready to transform your data
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ opacity: 0.7, mb: 1 }}
                >
                  Ask me to split rows, add calculations, or restructure your
                  spreadsheet
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    opacity: 0.5,
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                  }}
                >
                  {`Press ${
                    navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'
                  } + L to toggle`}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>

          <MDBox p={2} pt={1}>
            <MDBox display="flex" gap={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                maxRows={2}
                value={inputMessage}
                onChange={handleTextChange}
                placeholder="Describe how you want to transform your spreadsheet data..."
                disabled={isTransforming}
                size="small"
                error={!!transcribeAudioError}
                helperText={
                  transcribeAudioError
                    ? `Voice transcription error: ${transcribeAudioError.message}`
                    : undefined
                }
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                  },
                }}
              />
              <VoiceRecorder
                id="ai-assistant-voice-recorder-button"
                onRecordingComplete={handleRecordingComplete}
                isTranscribing={transcribeAudioLoading}
                loading={isTransforming}
                error={transcribeAudioError}
              />
              <Button
                id="ai-assistant-send-button"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isTransforming}
                sx={{
                  minWidth: '80px',
                  width: '80px',
                  height: '40px',
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  '& .MuiIcon-root': {
                    color: 'white !important',
                  },
                }}
              >
                {isTransforming ? (
                  <Icon
                    className="fa fa-spinner fa-spin"
                    sx={{
                      color: 'white !important',
                      fontSize: '18px',
                    }}
                  />
                ) : (
                  <Icon
                    sx={{
                      color: 'white !important',
                      fontSize: '18px',
                    }}
                  >
                    send
                  </Icon>
                )}
              </Button>
            </MDBox>
          </MDBox>
        </div>
      </Rnd>
    );
  },
  areEqual,
);

AIAssistantChat.displayName = 'AIAssistantChat';

export default AIAssistantChat;
